import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { ButtonModule, CheckboxModule, InputModule, RadioModule, SelectModule, TextareaModule } from '@sag-ui';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
    LargeInstrumentWarehouseDiscrepancyForm,
    LargeInstrumentWarehouseDiscrepancyFormGroup
} from '../../../models/cod-create.model';
import { AttachmentsInputComponent } from '../../../../shared/components/attachments-input/attachments-input.component';
import { ItemCondition } from '@cod-monorepo/interfaces';
import { ItemConditionMapping } from '../../../../shared/mappings/item-condition.mapping';
import { LargeInstrumentWarehouseIssueType } from '@cod-monorepo/interfaces';
import { PackageTypeCondition } from '@cod-monorepo/interfaces';
import { WarehousePackageType } from '@cod-monorepo/interfaces';
import { Store } from '@ngxs/store';
import { filter, tap } from 'rxjs';
import { AddCurrentCodLine, AddErrorMessage, RemoveErrorMessage } from '../../../state/cod.action';
import { WorkflowError } from '../../../../shared/enums/workflow-error';
import { ManagerSelectComponent } from '../../../../shared/components/manager-select/manager-select.component';

@Component({
    selector: 'app-cod-large-instrument-warehouse-discrepancy-step',
    templateUrl: './large-instrument-warehouse-discrepancy.component.html',
    imports: [
        CommonModule,
        SelectModule,
        InputModule,
        FormsModule,
        ReactiveFormsModule,
        RadioModule,
        CheckboxModule,
        ButtonModule,
        TextareaModule,
        AttachmentsInputComponent,
        ManagerSelectComponent
    ],
    standalone: true
})
export class CodLargeInstrumentWarehouseDiscrepancyStepComponent implements OnInit {
    @Input() largeInstrumentWarehouseDiscrepancyForm:
        | FormGroup<LargeInstrumentWarehouseDiscrepancyFormGroup>
        | undefined;

    private relatedCallTrackingNumberTouched = false;
    public largeInstrucmentWarehouseDiscrepancies = LargeInstrumentWarehouseIssueType;
    public packageTypes = WarehousePackageType;
    public packageTypeConditions = PackageTypeCondition;
    public itemConditions = ItemCondition;
    public itemConditionMapping = ItemConditionMapping;

    constructor(private store: Store) { }

    ngOnInit(): void {
        this.initCallTagTrackingNumberTouchedListener();
        this.initRelatedDocumentsListener();
        this.initFinancialAdjustmentChangesListener();
    }

    public isTrue(control: keyof LargeInstrumentWarehouseDiscrepancyForm): boolean {
        return this.largeInstrumentWarehouseDiscrepancyForm?.controls[control].value === true;
    }

    public isFalse(control: keyof LargeInstrumentWarehouseDiscrepancyForm): boolean {
        return this.largeInstrumentWarehouseDiscrepancyForm?.controls[control].value === false;
    }

    public itemConditionSelected(condition: ItemCondition): boolean {
        return this.largeInstrumentWarehouseDiscrepancyForm?.controls.itemCondition.value?.includes(condition) ?? false;
    }

    public toggleItemCondition(condition: ItemCondition): void {
        const itemCondition = this.largeInstrumentWarehouseDiscrepancyForm?.controls.itemCondition;

        if (itemCondition) {
            const conditions = itemCondition.value ?? [];

            if (conditions.includes(condition)) {
                itemCondition.setValue(conditions.filter(c => c !== condition));
            } else {
                itemCondition.setValue([...conditions, condition]);
            }
        }
    }

    public handleConfirm() {
        if (this.largeInstrumentWarehouseDiscrepancyForm?.valid) {
            this.store.dispatch(new AddCurrentCodLine());
        }
    }

    public isValid(): boolean {
        return this.largeInstrumentWarehouseDiscrepancyForm?.valid ?? false;
    }

    private initFinancialAdjustmentChangesListener(): void {
        this.largeInstrumentWarehouseDiscrepancyForm?.controls.financialAdjustment.valueChanges
            .pipe(
                filter(value => value === false),
                tap(() => {
                    this.largeInstrumentWarehouseDiscrepancyForm?.controls.adjustmentAmount.setValue(0);
                    this.largeInstrumentWarehouseDiscrepancyForm?.controls.filedWithin30Days.setValue(null);
                    this.largeInstrumentWarehouseDiscrepancyForm?.controls.managerApprovedRequest.setValue(null);
                }))
            .subscribe();
    }

    private initCallTagTrackingNumberTouchedListener(): void {
        this.largeInstrumentWarehouseDiscrepancyForm?.controls.callTagTrackingNumber.valueChanges
            .pipe(
                filter(() => !this.relatedCallTrackingNumberTouched),
                tap(() => {
                    this.store.dispatch(new AddErrorMessage(WorkflowError.UPLOAD_PROOF));

                    this.relatedCallTrackingNumberTouched = true;
                })
            )
            .subscribe();
    }

    private initRelatedDocumentsListener(): void {
        this.largeInstrumentWarehouseDiscrepancyForm?.controls.relatedDocuments.valueChanges
            .pipe(
                filter(value => !!value && value.length > 0),
                tap(() => this.store.dispatch(new RemoveErrorMessage(WorkflowError.UPLOAD_PROOF)))
            )
            .subscribe();
    }
}
